import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import assessMeImg from "../../assets/images/assessMeimg.png";
import userImg from "../../assets/images/user.png";
import { useNavigate } from "react-router-dom";
import userProfileImage from "../../assets/images/Profile-thumbnail-small.svg";
import ReactMarkdown from "react-markdown";
import {
  studentAssessMeAPI,
  studentGrowthCoordinatorAPI,
  getAssessMeHistoryAPI,
} from "../../redux/features/studentAiSlice";
import { Alert, Image } from "react-bootstrap";

const aiSuggestionMess = [
  "Are you aware of the networking opportunities that come with this program?",
  "“What tasks do you usually perform on a computer or",
  "Would you like to discuss the different pathways available for achieving your goals?",
  "Are you aware of the networking opportunities that come with this program?",
];

const StudentAssessMe = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth);

  const {
    isLoading = false,
    isError = null,
    message = null,
    conversationHistory = null,
  } = useSelector((state) => state.studentAI || {});
  const token = useSelector((state) => state.auth.token);
  const [input, setInput] = useState("");
  const [history, setHistory] = useState([]);
  const chatContainerRef = useRef(null);
  const textareaRef = useRef(null);

  // Audio recording states
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [recognition, setRecognition] = useState(null);
  const [audioDuration, setAudioDuration] = useState(0);
  const [recordingStartTime, setRecordingStartTime] = useState(null);
  const [transcribedText, setTranscribedText] = useState("");

  // Assessment completion states
  const [isAssessmentComplete, setIsAssessmentComplete] = useState(false);
  const [assessmentSummary, setAssessmentSummary] = useState(null);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [showManualCompleteModal, setShowManualCompleteModal] = useState(false);
  const [completionConfidence, setCompletionConfidence] = useState(0);

  // Load conversation history on component mount
  useEffect(() => {
    if (token) {
      dispatch(getAssessMeHistoryAPI({ token, page: 1, limit: 50 }));
    }
  }, [token, dispatch]);

  // Update local history when conversation history is loaded
  useEffect(() => {
    if (conversationHistory && conversationHistory.sessions) {
      // Convert database format to local history format
      const allMessages = [];
      const messageMap = new Map(); // To prevent duplicates

      conversationHistory.sessions.forEach(session => {
        session.messages.forEach(message => {
          // Create unique key for each message to avoid duplicates
          const messageKey = `${message.type}-${message.timestamp}-${message.content?.substring(0, 50)}`;
          if (!messageMap.has(messageKey)) {
            messageMap.set(messageKey, {
              type: message.type,
              text: message.content,
              timestamp: message.timestamp,
              sessionId: session.sessionId,
              assessmentType: session.assessmentType
            });
          }
        });
      });

      // Convert map to array and sort by timestamp
      const uniqueMessages = Array.from(messageMap.values());
      uniqueMessages.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

      setHistory(uniqueMessages);

      // Scroll to bottom after history is loaded - ensure scroll to last AI response
      const scrollToBottom = () => {
        if (chatContainerRef.current) {
          // Scroll to absolute bottom to show last message completely
          chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
      };

      // Multiple attempts to ensure scroll works after DOM updates
      setTimeout(scrollToBottom, 100);
      setTimeout(scrollToBottom, 300);
      setTimeout(scrollToBottom, 600);
      requestAnimationFrame(() => {
        setTimeout(scrollToBottom, 150);
        setTimeout(scrollToBottom, 450);
      });
    }
  }, [conversationHistory]);

  // Auto-scroll to bottom when history changes - especially after AI response
  useEffect(() => {
    if (chatContainerRef.current && history.length > 0) {
      // Check if last message is AI response
      const lastMessage = history[history.length - 1];
      const isLastMessageAI = lastMessage && lastMessage.type === 'ai';

      const scrollToBottom = () => {
        if (chatContainerRef.current) {
          // Always scroll to absolute bottom
          chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
      };

      // If last message is AI response, use multiple attempts to ensure full scroll
      if (isLastMessageAI) {
        requestAnimationFrame(() => {
          setTimeout(scrollToBottom, 100);
          setTimeout(scrollToBottom, 300);
          setTimeout(scrollToBottom, 500);
        });
      } else {
        // For other messages, normal scroll
        requestAnimationFrame(() => {
          setTimeout(scrollToBottom, 50);
          setTimeout(scrollToBottom, 200);
        });
      }
    }
  }, [history.length]);

  // Timer for recording duration
  useEffect(() => {
    let interval = null;
    if (isRecording && recordingStartTime) {
      interval = setInterval(() => {
        setAudioDuration(Math.floor((Date.now() - recordingStartTime) / 1000));
      }, 1000);
    } else {
      setAudioDuration(0);
    }
    return () => clearInterval(interval);
  }, [isRecording, recordingStartTime]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      const newHeight = Math.min(textareaRef.current.scrollHeight, 200);
      textareaRef.current.style.height = `${newHeight}px`;
    }
  }, [input]);

  const handleInputChange = (e) => {
    setInput(e.target.value);
  };

  // Check if assessment is complete based on AI response content only
  const checkAssessmentComplete = (aiResponse) => {
    // Check if AI response contains completion indicators
    const completionKeywords = [
      'assessment complete',
      'initial assessment finished',
      'evaluation complete',
      'assessment summary',
      'recommendations provided',
      'ready for growth coordinator',
      'assessment concluded',
      'proceed to growth coordinator'
    ];

    const responseText = aiResponse.toLowerCase();
    const hasCompletionKeywords = completionKeywords.some(keyword =>
      responseText.includes(keyword)
    );

    // Only check completion keywords - AI will decide when to complete
    return hasCompletionKeywords;
  };

  const handleSubmit = async () => {
    if (!input.trim()) return;
    if (!token) {
      dispatch({
        type: "studentAI/studentAssessMeAPI/rejected",
        payload: "Authentication required: Please log in to continue.",
      });
      navigate("/login");
      return;
    }

    // Add user message immediately to local history
    const userMessage = {
      type: "user",
      text: input,
      timestamp: new Date().toISOString()
    };
    setHistory(prev => [...prev, userMessage]);

    // Scroll to bottom after adding user message
    setTimeout(() => {
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
      }
    }, 50);

    try {
      const result = await dispatch(
        studentAssessMeAPI({ token, input })
      ).unwrap();
      if (result.success) {
        const responseData = result.data;

        // Handle error responses
        if (responseData?.error) {
          const errorMessage = {
            type: "error",
            text: responseData.response || "An error occurred. Please try again.",
            timestamp: new Date().toISOString()
          };
          setHistory(prev => [...prev, errorMessage]);
          return;
        }

        // Handle inappropriate content responses
        if (responseData?.isInappropriate) {
          const warningMessage = {
            type: "warning",
            text: responseData.response || "Please keep the conversation professional.",
            timestamp: new Date().toISOString()
          };
          setHistory(prev => [...prev, warningMessage]);
          return;
        }

        // Add AI response to local history - check for duplicates first
        const aiMessage = {
          type: "ai",
          text: responseData?.response || "No response content received.",
          sessionId: responseData?.sessionId,
          assessmentType: responseData?.assessmentType,
          completionConfidence: responseData?.completionConfidence || 0,
          isAssessmentComplete: responseData?.isAssessmentComplete || false,
          responsePattern: responseData?.responsePattern || 'general',
          timestamp: new Date().toISOString()
        };

        // Check if this message already exists to prevent duplicates
        setHistory(prev => {
          const messageExists = prev.some(msg =>
            msg.type === 'ai' &&
            msg.text === aiMessage.text &&
            Math.abs(new Date(msg.timestamp) - new Date(aiMessage.timestamp)) < 5000 // Within 5 seconds
          );
          if (messageExists) {
            return prev; // Don't add duplicate
          }
          return [...prev, aiMessage];
        });

        // Scroll to bottom after adding AI response - ensure full scroll to AI response end
        const scrollToBottom = () => {
          if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
          }
        };

        // Multiple attempts to ensure scroll reaches AI response end
        setTimeout(scrollToBottom, 100);
        setTimeout(scrollToBottom, 300);
        setTimeout(scrollToBottom, 600);
        requestAnimationFrame(() => {
          setTimeout(scrollToBottom, 150);
          setTimeout(scrollToBottom, 450);
        });

        // Track current session
        setCurrentSessionId(responseData?.sessionId);

        // Update completion confidence
        setCompletionConfidence(responseData?.completionConfidence || 0);

        // Check if assessment is complete using both AI response and confidence score
        const isComplete = responseData?.isAssessmentComplete || checkAssessmentComplete(aiMessage.text);
        if (isComplete && !isAssessmentComplete) {
          setIsAssessmentComplete(true);
          setAssessmentSummary(aiMessage.text);
        }

        // Don't refresh history immediately - let it update naturally to prevent duplicates
        // dispatch(getAssessMeHistoryAPI({ token, page: 1, limit: 50 }));
      }
    } catch (err) {
      // Error is handled by the slice's rejected case
    } finally {
      setInput("");
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }
  };

  const handleClear = () => {
    setHistory([]);
    setIsAssessmentComplete(false);
    setAssessmentSummary(null);
    setCurrentSessionId(null);
    setCompletionConfidence(0);
    setShowManualCompleteModal(false);
    dispatch({
      type: "studentAI/studentGrowthCoordinatorAPI/rejected",
      payload: null,
    });
  };

  // Manual completion handler
  const handleManualComplete = () => {
    setShowManualCompleteModal(true);
  };

  const confirmManualComplete = () => {
    setIsAssessmentComplete(true);
    setAssessmentSummary("Assessment completed manually by user.");
    setShowManualCompleteModal(false);
  };

  const cancelManualComplete = () => {
    setShowManualCompleteModal(false);
  };

  // Audio recording functions
  const startRecording = () => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();

      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      recognition.onstart = () => {
        setIsRecording(true);
        setRecordingStartTime(Date.now());
        setAudioDuration(0);
      };

      recognition.onresult = (event) => {
        let finalTranscript = '';
        let interimTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }

        // Update the transcribed text
        setTranscribedText(finalTranscript || interimTranscript);
      };

      recognition.onerror = (error) => {
        console.error('Speech recognition error:', error);
        setIsRecording(false);
        setRecordingStartTime(null);
        alert('Speech recognition error. Please try again.');
      };

      recognition.onend = () => {
        setIsRecording(false);
        setRecordingStartTime(null);
      };

      recognition.start();
      setRecognition(recognition);
    } else {
      alert('Speech recognition not supported in this browser. Please type your message.');
    }
  };

  const stopRecording = () => {
    if (recognition) {
      recognition.stop();
      setRecognition(null);
    }
    setIsRecording(false);
    setRecordingStartTime(null);
  };

  const sendAudioMessage = async () => {
    try {
      setIsProcessing(true);

      // Add user message with audio indicator (like WhatsApp) - but don't show transcribed text
      const userMessage = {
        type: "user",
        text: `🎤 Audio message (${audioDuration}s)`,
        timestamp: new Date().toISOString(),
        isAudio: true,
        audioDuration: audioDuration
      };
      setHistory(prev => [...prev, userMessage]);

      // Scroll to bottom after adding audio message
      setTimeout(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
      }, 50);

      // Send the ACTUAL transcribed text to AI (not the display text)
      const textToSend = transcribedText.trim() || "I sent an audio message";
      const result = await dispatch(
        studentAssessMeAPI({ token, input: textToSend })
      ).unwrap();

      if (result.success) {
        const aiMessage = {
          type: "ai",
          text: result.data?.response || "No response content received.",
          sessionId: result.data?.sessionId,
          assessmentType: result.data?.assessmentType,
          timestamp: new Date().toISOString()
        };

        // Check if this message already exists to prevent duplicates
        setHistory(prev => {
          const messageExists = prev.some(msg =>
            msg.type === 'ai' &&
            msg.text === aiMessage.text &&
            Math.abs(new Date(msg.timestamp) - new Date(aiMessage.timestamp)) < 5000 // Within 5 seconds
          );
          if (messageExists) {
            return prev; // Don't add duplicate
          }
          return [...prev, aiMessage];
        });

        // Scroll to bottom after adding AI response - ensure full scroll to AI response end
        const scrollToBottom = () => {
          if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
          }
        };

        // Multiple attempts to ensure scroll reaches AI response end
        setTimeout(scrollToBottom, 100);
        setTimeout(scrollToBottom, 300);
        setTimeout(scrollToBottom, 600);
        requestAnimationFrame(() => {
          setTimeout(scrollToBottom, 150);
          setTimeout(scrollToBottom, 450);
        });

        // Track current session
        setCurrentSessionId(result.data?.sessionId);

        // Check if assessment is complete (only based on AI response)
        const isComplete = checkAssessmentComplete(aiMessage.text);
        if (isComplete && !isAssessmentComplete) {
          setIsAssessmentComplete(true);
          setAssessmentSummary(aiMessage.text);
        }

        // Don't refresh history immediately - avoid duplicates
        // History will be updated when component remounts or user navigates
      }
    } catch (error) {
      console.error('Error sending audio message:', error);
      alert('Error sending message. Please try again.');
    } finally {
      setIsProcessing(false);
      setAudioDuration(0);
      setTranscribedText("");
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }
  };


  const handleMicrophoneClick = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const handleSendClick = () => {
    if (isRecording) {
      // If currently recording, stop and send
      stopRecording();
      sendAudioMessage();
    } else if (audioDuration > 0) {
      // If we have recorded audio, send it
      sendAudioMessage();
    } else {
      // Normal text message
      handleSubmit();
    }
  };

  if (!useSelector((state) => state.studentAI)) {
    return (
      <div className="text-center text-red-600">
        Error: Student AI reducer not found. Please check Redux store
        configuration.
      </div>
    );
  }
  return (
    <>
      <style>
        {`
          .wave-animation {
            display: flex;
            align-items: center;
            gap: 2px;
          }
          
          .wave-bar {
            width: 3px;
            height: 20px;
            background-color: #dc3545;
            border-radius: 2px;
            animation: wave 1.2s ease-in-out infinite;
          }
          
          .wave-bar:nth-child(1) { animation-delay: 0s; }
          .wave-bar:nth-child(2) { animation-delay: 0.1s; }
          .wave-bar:nth-child(3) { animation-delay: 0.2s; }
          .wave-bar:nth-child(4) { animation-delay: 0.3s; }
          .wave-bar:nth-child(5) { animation-delay: 0.4s; }
          
          @keyframes wave {
            0%, 40%, 100% {
              transform: scaleY(0.4);
            }
            20% {
              transform: scaleY(1);
            }
          }
        `}
      </style>
      <div className="card-default-style px-10 px-sm-20 px-md-30 px-lg-50 py-10 py-sm-15 py-md-20 py-lg-30">
        <div className="certifyMe text-center">
          <div className="certifyMe-inner mx-auto">
            <div className="chat-header mx-auto">
              <img
                src={assessMeImg}
                className="img-fluid img mx-auto"
                alt="Question Marks"
              />
              <h2 className="fw-bold my-21 ah-heading-04">AssessMe.AI</h2>
              <p className="ah-text-base fw-normal px-10 px-sm-20 px-md-30 text-gray-600">
                Initial competency assessment system. Evaluate technical skills,
                learning adaptability, and professional readiness. Provide structured
                feedback and personalized learning recommendations based on your responses.
              </p>

              {/* Progress Indicator - Only show if not already complete */}
              {!isAssessmentComplete && history.length > 0 && (
                <div className="mx-auto mt-3">
                  {/* Completion Confidence Indicator */}
                  {completionConfidence > 0 && (
                    <div className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <small className="text-muted">Assessment Progress</small>
                        <small className="text-muted">{completionConfidence}% Complete</small>
                      </div>
                      <div className="progress" style={{ height: '8px' }}>
                        <div
                          className="progress-bar bg-theme"
                          role="progressbar"
                          style={{ width: `${completionConfidence}%` }}
                        ></div>
                      </div>
                    </div>
                  )}

                  <div className="d-flex justify-content-between align-items-center">
                    <small className="text-muted flex-grow-1">
                      {history.filter(msg => msg.type === 'user').length < 6
                        ? 'Keep answering questions to complete your assessment'
                        : 'Almost there! A few more questions to complete your assessment'
                      }
                    </small>

                    {/* Manual Completion Button */}
                    {history.filter(msg => msg.type === 'user').length >= 3 && (
                      <button
                        className="btn btn-outline-primary no-shadow btn-sm"
                        onClick={handleManualComplete}
                        title="Complete assessment manually"
                      >
                        Complete Assessment
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Assessment Completion Banner */}
              {isAssessmentComplete && (
                <div className="alert alert-success mx-auto mt-3" style={{ maxWidth: '600px' }}>
                  <div className="d-flex align-items-center">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="me-2">
                      <path d="M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z" fill="#28a745" />
                    </svg>
                    <div className="flex-grow-1">
                      <h6 className="mb-1 fw-bold text-success">Assessment Complete!</h6>
                      <p className="mb-2 text-success">Your initial assessment has been completed. You can now proceed to the Growth Coordinator for personalized learning recommendations.</p>
                      <button
                        className="btn btn-success btn-sm"
                        onClick={() => navigate('/growth-coordinator')}
                      >
                        Go to Growth Coordinator →
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Chat or Suggested Messages */}
            <div className="mt-32">
              {history.length === 0 ? (
                <div className="suggested-messages mx-auto">
                  <div className="row ">
                    {aiSuggestionMess.map((question, index) => (
                      <div key={index} className="col-12 col-md-6 col-lg-3 mb-20">
                        <div
                          className="px-12 py-12 py-sm-22 h-100 d-flex flex-column card-green cursor-pointer"
                          onClick={() => setInput(question)}
                        >
                          <p className="ah-text-sm fw-normal text-gray-600 mb-0">
                            {question}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div
                  className="chat-container px-4 py-2"
                >
                  <div className="chat-wrapper" ref={chatContainerRef}>
                    {history.map((message, index) => (
                      <div
                        key={index}
                        className={`message ${message.type === "user" ? "user-message" : "bot-message"
                          }`}
                      >
                        {message.type === "user" ? (
                          <>
                            <div>
                              <Image
                                src={
                                  auth.profilePic?.startsWith("/")
                                    ? process.env.REACT_APP_GALLERY_URL +
                                    "/" +
                                    auth.profilePic
                                    : process.env.REACT_APP_GALLERY_URL +
                                    auth.profilePic
                                }
                                alt="Profile"
                                className="user-img"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = userProfileImage;
                                }}
                              />
                              {/* <img
                              className="user-img"
                              src={userImg}
                              alt="User"
                            /> */}
                            </div>
                            <div className="user-bubble text-end">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <span>You</span>
                                <small className="text-muted">
                                  {message.timestamp ? new Date(message.timestamp).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }) : ''}
                                </small>
                              </div>
                              {message.isAudio ? (
                                <div className="d-flex align-items-center gap-2 p-2 bg-light rounded">
                                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 14C13.66 14 15 12.66 15 11V5C15 3.34 13.66 2 12 2C10.34 2 9 3.34 9 5V11C9 12.66 10.34 14 12 14Z" fill="#007bff" />
                                    <path d="M19 11C19 15.42 15.42 19 11 19H13C17.42 19 21 15.42 21 11H19Z" fill="#007bff" />
                                    <path d="M5 11C5 15.42 8.58 19 13 19H11C6.58 19 3 15.42 3 11H5Z" fill="#007bff" />
                                  </svg>
                                  <div>
                                    <div className="fw-bold text-primary">Audio Message</div>
                                    <small className="text-muted">{message.audioDuration || 0}s</small>
                                  </div>
                                </div>
                              ) : (
                                message.text.split("\n").map((line, i) => (
                                  <p key={i} className="mb-2">
                                    {line}
                                  </p>
                                ))
                              )}
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="bot-icon">
                              <svg
                                width="20"
                                height="21"
                                viewBox="0 0 20 21"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M19 9.81814V6.84314C19 6.31271 18.7893 5.804 18.4142 5.42893C18.0391 5.05386 17.5304 4.84314 17 4.84314H11V3.53114C11.305 3.25714 11.5 2.86314 11.5 2.42114C11.5 2.02332 11.342 1.64179 11.0607 1.36048C10.7794 1.07918 10.3978 0.921143 10 0.921143C9.60218 0.921143 9.22064 1.07918 8.93934 1.36048C8.65804 1.64179 8.5 2.02332 8.5 2.42114C8.5 2.86314 8.695 3.25714 9 3.53114V4.84314H3C2.46957 4.84314 1.96086 5.05386 1.58579 5.42893C1.21071 5.804 1 6.31271 1 6.84314V9.84114L0.928 9.84614C0.675973 9.86434 0.440176 9.97726 0.268018 10.1622C0.0958586 10.3472 0.000102245 10.5905 0 10.8431V12.8431C0 13.1084 0.105357 13.3627 0.292893 13.5502C0.48043 13.7378 0.734784 13.8431 1 13.8431V18.8431C1 19.3736 1.21071 19.8823 1.58579 20.2574C1.96086 20.6324 2.46957 20.8431 3 20.8431H17C17.5304 20.8431 18.0391 20.6324 18.4142 20.2574C18.7893 19.8823 19 19.3736 19 18.8431V13.8431C19.2652 13.8431 19.5196 13.7378 19.7071 13.5502C19.8946 13.3627 20 13.1084 20 12.8431V10.9051C20.0117 10.7499 19.9871 10.5941 19.928 10.4501C19.726 9.96214 19.293 9.84514 19 9.81814ZM5 10.8431C5 9.73914 5.672 8.84314 6.5 8.84314C7.328 8.84314 8 9.73914 8 10.8431C8 11.9471 7.328 12.8431 6.5 12.8431C5.672 12.8431 5 11.9471 5 10.8431ZM13.998 16.8431C12.997 16.8401 6 16.8431 6 16.8431V14.8431C6 14.8431 13.001 14.8411 14.002 14.8431L13.998 16.8431ZM13.5 12.8431C12.672 12.8431 12 11.9471 12 10.8431C12 9.73914 12.672 8.84314 13.5 8.84314C14.328 8.84314 15 9.73914 15 10.8431C15 11.9471 14.328 12.8431 13.5 12.8431Z"
                                  fill="white"
                                />
                              </svg>
                            </div>
                            <div className="bot-bubble d-flex flex-column text-start">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <span>AssessMe.AI</span>
                                <small className="text-muted">
                                  {message.timestamp ? new Date(message.timestamp).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }) : ''}
                                </small>
                              </div>
                              <ReactMarkdown>{message.text}</ReactMarkdown>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Input and Submit Button */}
            {isLoading && (
              <p className="text-center mt-10">
                <div className="dottedLoading theme-color"></div>
              </p>
            )}
            {isError && (
              <Alert variant="danger" >{isError}</Alert>
            )}


            <div className="certifyMe-search-box-wrapper mx-auto mt-22 overflow-hidden px-9 py-17 mb-32">
              <div className="position-relative d-flex align-items-center">
                {/* Wave Animation - Left side of input */}
                {isRecording && (
                  <div className="wave-animation position-absolute" style={{ left: '50%', transform: 'translateX(-50%)', zIndex: 10 }}>
                    <div className="wave-bar"></div>
                    <div className="wave-bar"></div>
                    <div className="wave-bar"></div>
                    <div className="wave-bar"></div>
                    <div className="wave-bar"></div>
                  </div>
                )}

                <textarea
                  ref={textareaRef}
                  className={`form-control ah-text-base text-black flex-grow-1 ${isRecording ? 'ps-5' : ''}`}
                  placeholder={isRecording ? `Recording... ${audioDuration}s` : "Type your message..."}
                  value={input}
                  onChange={handleInputChange}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSendClick();
                    }
                  }}
                  disabled={isProcessing}
                  style={{
                    paddingLeft: isRecording ? '60px' : '15px',
                    paddingRight: '110px',
                    minHeight: '85px',
                    maxHeight: '200px',
                    resize: 'none',
                    overflowY: 'auto',
                    lineHeight: '1.5',
                    paddingTop: '10px',
                    paddingBottom: '10px'
                  }}
                  rows={1}
                />

                {/* Microphone Button */}
                <button
                  className={`border-0 rounded-circle text-white py-5 mx-5 position-absolute ${isRecording ? 'bg-danger' : isProcessing ? 'bg-warning' : 'bg-secondary'
                    }`}
                  type="button"
                  onClick={handleMicrophoneClick}
                  disabled={isLoading || !token || isProcessing}
                  style={{ right: '60px' }}
                  title={isRecording ? 'Stop Recording' : isProcessing ? 'Processing...' : 'Click to speak'}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 14C13.66 14 15 12.66 15 11V5C15 3.34 13.66 2 12 2C10.34 2 9 3.34 9 5V11C9 12.66 10.34 14 12 14Z"
                      fill="white"
                    />
                    <path
                      d="M19 11C19 15.42 15.42 19 11 19H13C17.42 19 21 15.42 21 11H19Z"
                      fill="white"
                    />
                    <path
                      d="M5 11C5 15.42 8.58 19 13 19H11C6.58 19 3 15.42 3 11H5Z"
                      fill="white"
                    />
                    <path
                      d="M12 22C12.55 22 13 21.55 13 21V19H11V21C11 21.55 11.45 22 12 22Z"
                      fill="white"
                    />
                  </svg>
                </button>

                {/* Send Button */}
                <button
                  className={`border-0 rounded-circle text-white py-5 mx-10 position-absolute ${(isRecording || audioDuration > 0) ? 'bg-success' : 'bg-theme'
                    }`}
                  type="button"
                  onClick={handleSendClick}
                  disabled={isLoading || !token || isProcessing}
                  style={{ right: '10px' }}
                  title={isRecording ? 'Stop & Send Audio' : audioDuration > 0 ? 'Send Audio' : 'Send Message'}
                >
                  <svg
                    width="25"
                    height="25"
                    viewBox="0 0 25 25"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M10.2656 4.84314C9.4372 4.84314 8.76562 5.51471 8.76562 6.34314V19.3431C8.76562 20.1715 9.4372 20.8431 10.2656 20.8431C11.094 20.8431 11.7656 20.1715 11.7656 19.3431V6.34314C11.7656 5.51471 11.094 4.84314 10.2656 4.84314Z"
                      fill="white"
                    />
                    <path
                      d="M13.7656 9.34314C13.7656 8.51471 14.4372 7.84314 15.2656 7.84314C16.094 7.84314 16.7656 8.51471 16.7656 9.34314V16.3431C16.7656 17.1715 16.094 17.8431 15.2656 17.8431C14.4372 17.8431 13.7656 17.1715 13.7656 16.3431V9.34314Z"
                      fill="white"
                    />
                    <path
                      d="M5.26562 9.84314C4.4372 9.84314 3.76562 10.5147 3.76562 11.3431V14.3431C3.76562 15.1715 4.4372 15.8431 5.26562 15.8431C6.09405 15.8431 6.76562 15.1715 6.76562 14.3431V11.3431C6.76562 10.5147 6.09405 9.84314 5.26562 9.84314Z"
                      fill="white"
                    />
                    <path
                      d="M20.2656 9.84314C19.4372 9.84314 18.7656 10.5147 18.7656 11.3431V14.3431C18.7656 15.1715 19.4372 15.8431 20.2656 15.8431C21.094 15.8431 21.7656 15.1715 21.7656 14.3431V11.3431C21.7656 10.5147 21.094 9.84314 20.2656 9.84314Z"
                      fill="white"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Manual Completion Modal */}
      {showManualCompleteModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Complete Assessment</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={cancelManualComplete}
                ></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to complete the assessment manually?</p>
                <p className="text-muted small">
                  You have answered {history.filter(msg => msg.type === 'user').length} questions.
                  The system recommends at least 6-8 questions for a comprehensive assessment.
                </p>
                {completionConfidence > 0 && (
                  <div className="alert alert-info">
                    <strong>Current Progress:</strong> {completionConfidence}% complete
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={cancelManualComplete}
                >
                  Continue Assessment
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={confirmManualComplete}
                >
                  Complete Assessment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StudentAssessMe;
